/**
 * FilterPillStrip — horizontal scrollable row of dismissible active-filter
 * pills, rendered on the phone breakpoint (#704 PR-3).
 *
 * The mobile FilterPanel itself lives behind a full-screen FilterSheet (PR-3
 * sibling), so once the user closes that sheet there's no surface to remind
 * them what's currently constraining the map. This strip is that reminder
 * surface: each active filter renders as a labelled pill with an "x" to
 * clear that single filter without re-opening the sheet.
 *
 * Active filter detection rule
 * ----------------------------
 * "Active" means a key with a truthy / non-default value. The active
 * detector lives in ACTIVE_FILTER_DESCRIPTORS so it can be reused by the
 * Filters button (badge count) in SearchPage; export it from here rather
 * than duplicate the list. Each descriptor:
 *   - key     : the filters[key] this pill represents
 *   - label   : a fn(value, allFilters) -> short user-facing string
 *   - clearTo : value to set when the user dismisses the pill (usually '')
 *
 * Some pills represent a coupled pair (e.g. minArea + maxArea become a
 * single "Acres" pill). The descriptor handles that by reading multiple
 * keys in `label` and clearing multiple keys in `clearKeys`.
 *
 * Layout
 * ------
 * `overflow-x: auto` on the strip + `flex-shrink: 0` on each pill = horizontal
 * scroll on phone without wrapping into multiple rows. Hidden when the active
 * set is empty so the strip doesn't reserve vertical space for nothing.
 *
 * Not rendered on tablet/desktop. SearchPage decides whether to mount this
 * component based on useBreakpoint() — keeping the responsibility at the
 * page level rather than gating inside the component lets tests render the
 * strip directly without monkey-patching window width.
 */
import React from 'react';

/**
 * Format a $ amount as a short pill label: $250k, $1.2m, $42, "any".
 * Tighter than the popup price formatter because pills are space-constrained.
 */
function shortMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1)}m`;
  if (num >= 1_000) return `$${Math.round(num / 1_000)}k`;
  return `$${num}`;
}

/**
 * Format a numeric value as a "compact" label — used for acreage and DOM.
 * Drops trailing .0 and keeps at most one decimal place.
 */
function shortNum(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10_000 ? 0 : 1)}k`;
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
}

/**
 * Build a "min – max" range label from a low+high pair where either side
 * may be missing. Empty pair (both undefined) returns null so the caller
 * can skip the pill.
 */
function rangeLabel(low, high, format) {
  const hasLow = low !== undefined && low !== null && low !== '';
  const hasHigh = high !== undefined && high !== null && high !== '';
  if (!hasLow && !hasHigh) return null;
  if (hasLow && hasHigh) return `${format(low)} – ${format(high)}`;
  if (hasLow) return `≥ ${format(low)}`;
  return `≤ ${format(high)}`;
}

/**
 * Probability label for the 0..1 mineral filters — shown as a "%+" cutoff.
 */
function probLabel(label, value) {
  const pct = Math.round(Number(value) * 100);
  if (!Number.isFinite(pct)) return label;
  return `${label} ≥ ${pct}%`;
}

/**
 * The descriptor list. ORDER matters: pills render in this order so the
 * most-common filters (state, listing status, acres, price) appear leftmost.
 *
 * Each descriptor returns a label or `null` when not active. SearchPage's
 * Filters-button badge counts the number of non-null labels here.
 */
export const ACTIVE_FILTER_DESCRIPTORS = [
  {
    key: 'state',
    label: (filters) => (filters.state ? `State: ${filters.state}` : null),
    clearKeys: ['state'],
  },
  {
    key: 'listing',
    label: (filters) => {
      if (filters.isListed) return 'Listed only';
      if (filters.offMarketOnly) return 'Off-market only';
      return null;
    },
    clearKeys: ['isListed', 'offMarketOnly'],
  },
  {
    key: 'acres',
    label: (filters) => {
      const r = rangeLabel(filters.minArea, filters.maxArea, shortNum);
      return r ? `${r} ac` : null;
    },
    clearKeys: ['minArea', 'maxArea'],
  },
  {
    key: 'price',
    label: (filters) => {
      const r = rangeLabel(filters.minPrice, filters.maxPrice, shortMoney);
      return r ? `Price ${r}` : null;
    },
    clearKeys: ['minPrice', 'maxPrice'],
  },
  {
    key: 'pricePerAcre',
    label: (filters) => {
      const r = rangeLabel(filters.minPricePerAcre, filters.maxPricePerAcre, shortMoney);
      return r ? `$/ac ${r}` : null;
    },
    clearKeys: ['minPricePerAcre', 'maxPricePerAcre'],
  },
  {
    key: 'q',
    label: (filters) => (filters.q ? `“${filters.q}”` : null),
    clearKeys: ['q'],
  },
  {
    key: 'bookmarksOnly',
    label: (filters) => (filters.bookmarksOnly ? 'Bookmarks' : null),
    clearKeys: ['bookmarksOnly'],
  },
  {
    key: 'distressedOnly',
    label: (filters) => (filters.distressedOnly ? 'Distressed' : null),
    clearKeys: ['distressedOnly'],
  },
  {
    key: 'landOnlyConfirmed',
    label: (filters) => (filters.landOnlyConfirmed ? 'Land-only' : null),
    clearKeys: ['landOnlyConfirmed'],
  },
  {
    key: 'excludeLiens',
    label: (filters) => (filters.excludeLiens ? 'Hide liens' : null),
    clearKeys: ['excludeLiens'],
  },
  {
    key: 'excludeEasements',
    label: (filters) => (filters.excludeEasements ? 'Hide easements' : null),
    clearKeys: ['excludeEasements'],
  },
  {
    key: 'excludeLandlocked',
    label: (filters) => (filters.excludeLandlocked ? 'Hide landlocked' : null),
    clearKeys: ['excludeLandlocked'],
  },
  {
    key: 'domFilter',
    label: (filters) => (filters.domFilter ? `DOM ${filters.domFilter}` : null),
    clearKeys: ['domFilter'],
  },
  {
    key: 'priceChangedRecently',
    label: (filters) => {
      if (!filters.priceChangedRecently) return null;
      const d = filters.priceChangeDays || 30;
      return `Price cut ≤ ${d}d`;
    },
    clearKeys: ['priceChangedRecently', 'priceChangeDays'],
  },
  {
    key: 'maxWildfireRisk',
    label: (filters) => (filters.maxWildfireRisk
      ? `Wildfire < ${filters.maxWildfireRisk}`
      : null),
    clearKeys: ['maxWildfireRisk'],
  },
  {
    key: 'maxFloodRisk',
    label: (filters) => (filters.maxFloodRisk
      ? `Flood < ${filters.maxFloodRisk}`
      : null),
    clearKeys: ['maxFloodRisk'],
  },
  {
    key: 'elevationChange',
    label: (filters) => {
      const r = rangeLabel(filters.minElevationChange, filters.maxElevationChange, shortNum);
      return r ? `Elev Δ ${r} m` : null;
    },
    clearKeys: ['minElevationChange', 'maxElevationChange'],
  },
  {
    key: 'maxMineDist',
    label: (filters) => (filters.maxMineDist
      ? `Mine ≤ ${filters.maxMineDist} km`
      : null),
    clearKeys: ['maxMineDist'],
  },
  {
    key: 'minMinesNearby',
    label: (filters) => (filters.minMinesNearby
      ? `Mines ≥ ${filters.minMinesNearby}`
      : null),
    clearKeys: ['minMinesNearby'],
  },
  {
    // #997 alt-energy generation-site proximity. Includes the tech in the
    // label when set; clearing removes both the distance and the tech
    // restriction so no dangling `generation_tech` survives.
    key: 'maxGenerationDist',
    label: (filters) => (filters.maxGenerationDist
      ? `${filters.generationTech ? `${filters.generationTech[0].toUpperCase()}${filters.generationTech.slice(1)}` : 'Generation'} ≤ ${filters.maxGenerationDist} km`
      : null),
    clearKeys: ['maxGenerationDist', 'generationTech'],
  },
  {
    // #991 HIFLD transmission-line proximity. One pill summarizes all four
    // powerline keys (distance km + min voltage + volt class + line type);
    // clearing it removes every powerline filter at once. Shows when ANY
    // of the four is set, so the voltage/class/type filters (which stand
    // alone without a distance cap) still surface a pill.
    key: 'maxPowerlineDist',
    label: (filters) => {
      if (!(filters.maxPowerlineDist || filters.minPowerlineVoltage
        || filters.powerlineVoltClass || filters.powerlineLineType)) return null;
      const parts = [];
      if (filters.powerlineLineType) parts.push(filters.powerlineLineType);
      parts.push('Transmission');
      if (filters.powerlineVoltClass) parts.push(`${filters.powerlineVoltClass} class`);
      else if (filters.minPowerlineVoltage) parts.push(`≥ ${filters.minPowerlineVoltage} kV`);
      let s = parts.join(' ');
      if (filters.maxPowerlineDist) s += ` ≤ ${filters.maxPowerlineDist} km`;
      return s;
    },
    clearKeys: ['maxPowerlineDist', 'minPowerlineVoltage', 'powerlineVoltClass', 'powerlineLineType'],
  },
  {
    key: 'minGoldProb',
    label: (filters) => (filters.minGoldProb ? probLabel('Au', filters.minGoldProb) : null),
    clearKeys: ['minGoldProb'],
  },
  {
    key: 'minCopperProb',
    label: (filters) => (filters.minCopperProb ? probLabel('Cu', filters.minCopperProb) : null),
    clearKeys: ['minCopperProb'],
  },
  {
    key: 'minSilverProb',
    label: (filters) => (filters.minSilverProb ? probLabel('Ag', filters.minSilverProb) : null),
    clearKeys: ['minSilverProb'],
  },
  {
    key: 'minUraniumProb',
    label: (filters) => (filters.minUraniumProb ? probLabel('U', filters.minUraniumProb) : null),
    clearKeys: ['minUraniumProb'],
  },
  {
    key: 'minLithiumProb',
    label: (filters) => (filters.minLithiumProb ? probLabel('Li', filters.minLithiumProb) : null),
    clearKeys: ['minLithiumProb'],
  },
  {
    key: 'minReeProb',
    label: (filters) => (filters.minReeProb ? probLabel('REE', filters.minReeProb) : null),
    clearKeys: ['minReeProb'],
  },
  {
    // Niobium (#990) — parity with the six commodities above.
    key: 'minNiobiumProb',
    label: (filters) => (filters.minNiobiumProb ? probLabel('Nb', filters.minNiobiumProb) : null),
    clearKeys: ['minNiobiumProb'],
  },
];

/**
 * Count how many descriptors emit a non-null label for the given filters.
 * Exported so SearchPage's Filters-button badge can stay in lock-step with
 * the strip (one source of truth for "what counts as active").
 */
export function countActiveFilters(filters) {
  if (!filters || typeof filters !== 'object') return 0;
  let count = 0;
  for (const d of ACTIVE_FILTER_DESCRIPTORS) {
    if (d.label(filters) != null) count += 1;
  }
  return count;
}

/**
 * Build the list of active pills (descriptor + computed label). Memoizable
 * but the list is small (≤25) so we just recompute on every render.
 */
function buildActivePills(filters) {
  const pills = [];
  if (!filters || typeof filters !== 'object') return pills;
  for (const d of ACTIVE_FILTER_DESCRIPTORS) {
    const label = d.label(filters);
    if (label != null) {
      pills.push({ key: d.key, label, clearKeys: d.clearKeys });
    }
  }
  return pills;
}

function FilterPillStrip({ filters, setFilters }) {
  const pills = buildActivePills(filters);

  if (pills.length === 0) {
    // Render an empty marker div so SearchPage can use the strip's presence
    // as a layout anchor (consistent padding) without dragging in an
    // additional wrapper. data-testid lets tests assert "strip is empty
    // but mounted" if needed.
    return (
      <div
        className="filter-pill-strip filter-pill-strip--empty"
        data-testid="filter-pill-strip"
        aria-hidden="true"
      />
    );
  }

  const onClear = (clearKeys) => {
    setFilters((prev) => {
      const next = { ...prev };
      for (const k of clearKeys) {
        // For booleans we set to false; for everything else to ''.
        // The map / list filters treat both as "no constraint" via
        // their `filters[key] ? ... : undefined` guards.
        if (typeof prev[k] === 'boolean') next[k] = false;
        else next[k] = '';
      }
      return next;
    });
  };

  return (
    <div
      className="filter-pill-strip"
      data-testid="filter-pill-strip"
      role="list"
      aria-label="Active filters"
    >
      {pills.map((pill) => (
        <button
          type="button"
          key={pill.key}
          className="filter-pill"
          data-testid={`filter-pill-${pill.key}`}
          onClick={() => onClear(pill.clearKeys)}
          aria-label={`Clear ${pill.label}`}
          role="listitem"
        >
          <span className="filter-pill-label">{pill.label}</span>
          {/* The × is decorative; aria-label on the button already conveys
              the affordance. aria-hidden so screen readers don't read it
              as a separate item. */}
          <span className="filter-pill-close" aria-hidden="true">×</span>
        </button>
      ))}
    </div>
  );
}

export default FilterPillStrip;
